import React, { useState, useEffect } from 'react';
import { patientBookingAPI } from '../../services/api';
import { BookingListItem } from '../../types/booking';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { formatDuration, getDurationMinutes } from '../../utils/formatters';
import { formatBookingDateTime, formatEndTime } from '../../utils/timezone';
import Card from '../ui/Card';
import StatusBanner from '../ui/StatusBanner';
import { Modal, Button } from '../ui';
import { isInsideCancellationWindow, isWithinGrace, formatDeadline, formatGraceDeadline } from '../../constants/policy';
import { usePolicy } from '../../contexts/PolicyContext';
import { CalendarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

const MyBookings: React.FC = () => {
  const { windowHours: CANCELLATION_WINDOW_HOURS, graceMinutes: BOOKING_GRACE_MINUTES } = usePolicy();
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<BookingListItem | null>(null);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const bookingsData = await patientBookingAPI.getMyBookings();
      setBookings(bookingsData);
    } catch (error: any) {
      setError(apiErrorMessage(error, 'Failed to load bookings'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId: number) => {
    try {
      setCancellingId(bookingId);
      const res = await patientBookingAPI.cancelBooking(bookingId);
      if (res.late_cancellation) {
        toast(res.message, { icon: '⚠️' });
      } else {
        toast.success('Appointment cancelled');
      }
      setCancelTarget(null);
      await loadBookings();
    } catch (error: any) {
      toast.error(apiErrorMessage(error, 'Failed to cancel booking'));
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'bg-clinic-primary/20 text-clinic-primary';
      case 'pending':
        return 'bg-clinic-gold/20 text-clinic-gold';
      case 'cancelled':
        return 'bg-clinic-accent/20 text-clinic-accent';
      default:
        return 'bg-clinic-muted/20 text-clinic-muted';
    }
  };

  const canCancelBooking = (booking: BookingListItem) => {
    if (booking.status.toLowerCase() === 'cancelled') return false;
    // Allow cancellation anytime before the appointment. Inside the
    // cancellation window the server keeps the room held for the clinician.
    const startTime = new Date(booking.start_time);
    return startTime > new Date();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <StatusBanner
        variant="error"
        title="Unable to load appointments"
        message={error}
      />
    );
  }

  // Separate upcoming and past bookings
  const now = new Date();
  const upcomingBookings = bookings.filter(b =>
    new Date(b.start_time) > now && b.status.toLowerCase() !== 'cancelled'
  );
  const pastBookings = bookings.filter(b =>
    new Date(b.start_time) <= now || b.status.toLowerCase() === 'cancelled'
  );

  const cancelDialog = cancelTarget && (() => {
    const { date, time } = formatBookingDateTime(cancelTarget.start_time);
    const late = isInsideCancellationWindow(cancelTarget.start_time, new Date(), cancelTarget.created_at, CANCELLATION_WINDOW_HOURS, BOOKING_GRACE_MINUTES);
    const inGrace = isWithinGrace(cancelTarget.created_at, new Date(), BOOKING_GRACE_MINUTES);
    return (
      <Modal
        isOpen
        onClose={() => { if (cancellingId === null) setCancelTarget(null); }}
        title="Cancel appointment"
        description={`${cancelTarget.clinician_name} · ${date} · ${time}`}
        size="sm"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setCancelTarget(null)} disabled={cancellingId !== null}>
              Keep appointment
            </Button>
            <Button
              variant="danger"
              onClick={() => handleCancelBooking(cancelTarget.id)}
              isLoading={cancellingId === cancelTarget.id}
              disabled={cancellingId !== null}
            >
              Cancel appointment
            </Button>
          </>
        )}
      >
        {inGrace ? (
          <p className="text-sm text-clinic-dark">
            Booked this less than {BOOKING_GRACE_MINUTES} minutes ago? No problem. Cancelling now releases the time at no charge.
          </p>
        ) : late ? (
          <StatusBanner
            variant="warning"
            title={`Within ${CANCELLATION_WINDOW_HOURS} hours`}
            message="You can still cancel, but this is a late cancellation. Your clinician's room stays reserved, and their own cancellation policy may apply."
          />
        ) : (
          <p className="text-sm text-clinic-dark">
            This is a free cancellation (before {formatDeadline(cancelTarget.start_time, CANCELLATION_WINDOW_HOURS)}). Your clinician will be
            notified and the time is released.
          </p>
        )}
      </Modal>
    );
  })();

  return (
    <div className="space-y-6">
      {cancelDialog}
      {/* Upcoming Appointments */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-clinic-dark mb-4">Upcoming Appointments</h2>

        {upcomingBookings.length === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon className="h-12 w-12 text-clinic-muted mx-auto mb-3" />
            <p className="text-clinic-dark font-medium">No upcoming appointments</p>
            <p className="text-sm text-clinic-muted mt-1">Book your next appointment using the calendar tab</p>
          </div>
        ) : (
          <div className="space-y-4">
            {upcomingBookings.map((booking) => {
              const { date, time } = formatBookingDateTime(booking.start_time);
              const durationMins = getDurationMinutes(booking);
              const endTime = formatEndTime(booking.start_time, durationMins);
              const canCancel = canCancelBooking(booking);

              return (
                <div
                  key={booking.id}
                  className="border border-clinic-primary-lighter rounded-lg p-4 hover:bg-clinic-cream/30 transition-colors"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-clinic-dark">{date}</h3>
                      <p className="text-sm text-clinic-muted">
                        {time} - {endTime} ({formatDuration(durationMins)})
                      </p>
                      <p className="text-sm text-clinic-muted mt-1">
                        <strong>Clinician:</strong> {booking.clinician_name}
                      </p>
                      {booking.room_name && (
                        <p className="text-sm text-clinic-muted">
                          <strong>Room:</strong> {booking.room_name}
                        </p>
                      )}
                      {canCancel && (
                        <p className="text-xs text-clinic-muted mt-1">
                          {isWithinGrace(booking.created_at, new Date(), BOOKING_GRACE_MINUTES)
                            ? `Made a mistake? Free to cancel until ${formatGraceDeadline(booking.created_at as string, BOOKING_GRACE_MINUTES)}`
                            : isInsideCancellationWindow(booking.start_time, new Date(), booking.created_at, CANCELLATION_WINDOW_HOURS, BOOKING_GRACE_MINUTES)
                            ? `Inside ${CANCELLATION_WINDOW_HOURS} hours: late-cancellation policy applies`
                            : `Free cancellation until ${formatDeadline(booking.start_time, CANCELLATION_WINDOW_HOURS)}`}
                        </p>
                      )}
                    </div>

                    <div className="text-right flex flex-col items-end gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(booking.status)}`}>
                        {booking.status}
                      </span>

                      {canCancel && (
                        <button
                          onClick={() => setCancelTarget(booking)}
                          disabled={cancellingId === booking.id}
                          className="inline-flex items-center min-h-[44px] px-2 -mx-2 text-sm text-clinic-accent hover:text-clinic-accent/80 font-medium disabled:opacity-50"
                        >
                          {cancellingId === booking.id ? 'Cancelling...' : 'Cancel'}
                        </button>
                      )}

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Past Appointments */}
      {pastBookings.length > 0 && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-clinic-dark mb-4">Past Appointments</h2>

          <div className="space-y-4">
            {pastBookings.slice(0, 5).map((booking) => {
              const { date, time } = formatBookingDateTime(booking.start_time);
              const durationMins = getDurationMinutes(booking);
              const endTime = formatEndTime(booking.start_time, durationMins);

              return (
                <div
                  key={booking.id}
                  className="border border-clinic-primary-lighter rounded-lg p-4 opacity-60"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-clinic-dark">{date}</h3>
                      <p className="text-sm text-clinic-muted">
                        {time} - {endTime} ({formatDuration(durationMins)})
                      </p>
                      <p className="text-sm text-clinic-muted mt-1">
                        <strong>Clinician:</strong> {booking.clinician_name}
                      </p>
                      {booking.room_name && (
                        <p className="text-sm text-clinic-muted">
                          <strong>Room:</strong> {booking.room_name}
                        </p>
                      )}
                    </div>

                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>
                </div>
              );
            })}

            {pastBookings.length > 5 && (
              <p className="text-center text-sm text-clinic-muted py-2">
                Showing 5 most recent past appointments
              </p>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default MyBookings;
